import { platformById } from "@ondit/publishing";
import {
	Button,
	ChipInput,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Input,
	ModalDescription,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { Search } from "@ondit/ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import type { PublishingPlatform } from "~/core/api/distribution";
import { ApiError, api } from "~/core/api/index";
import { useTabParam } from "~/core/hooks";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Hint, Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { PlatformIdentity } from "~/modules/analytics/components/platform-identity";
import { ConnectAccount } from "./connect";

/**
 * What each platform accepts, and the account for the six Ondit sends to.
 *
 * A brand's settings rather than a tab of Distribution: the rules are read
 * once and revisited when a post is refused, and the account is set once —
 * neither is part of publishing an article.
 */
const GROUPS = ["direct", "manual"] as const;

export function PublishingPlatforms({ entityId }: { entityId: string }) {
	const t = useT();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	const accounts = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
	});
	const [search, setSearch] = useState("");
	// Tabs, because the two lists stacked ran to eight screens, and the six
	// that take an account sat above a reference nobody acts on from here.
	const [group, setGroup] = useTabParam(GROUPS);
	return (
		<Async state={state}>
			{(data) => {
				const shown = data.platforms.filter((p) =>
					`${p.name} ${p.domain}`.toLowerCase().includes(search.toLowerCase()),
				);
				// Split on whether Ondit can send, not on the registry's "API" mark:
				// a platform with an API nobody wrote a publisher for is still sent
				// by hand.
				const direct = shown.filter((p) => platformById(p.id)?.credential_provider);
				const manual = shown.filter((p) => !platformById(p.id)?.credential_provider);
				return (
					<Section
						title={t("publishing.heading")}
						lede={t("distribution.rulesHint")}
						action={<AddPlatform entityId={entityId} />}
					>
						<Tabs value={group} onValueChange={setGroup} className="publishing-settings">
							<div className="publishing-toolbar">
								<div className="title-row">
									<TabsList aria-label={t("settings.section.publishing")}>
										<TabsTrigger value="direct">
											{t("publishing.group.direct")} · {direct.length}
										</TabsTrigger>
										<TabsTrigger value="manual">
											{t("publishing.group.manual")} · {manual.length}
										</TabsTrigger>
									</TabsList>
									<Hint text={t(group === "direct" ? "publishing.group.directHint" : "publishing.group.manualHint")} />
								</div>
								<ChipInput
									className="ml-auto w-[calc(var(--s-14)*3.5)] max-w-full"
									icon={Search}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder={t("distribution.search")}
									aria-label={t("distribution.search")}
								/>
							</div>
							<TabsContent value="direct">
								<ul className="publishing-platforms">
									{direct.map((p) => (
										<PlatformRow key={p.id} platform={p}>
											<Account entityId={entityId} platform={p} accounts={accounts.data?.accounts} />
										</PlatformRow>
									))}
								</ul>
							</TabsContent>
							<TabsContent value="manual">
								<ul className="publishing-platforms">
									{manual.map((p) => (
										<PlatformRow key={p.id} platform={p}>
											<span className="hint">
												{(p.publishing_methods ?? ["manual"])
													.filter((m) => m !== "api")
													.map((m) => t(`distribution.method.${m}`))
													.join(" · ") || t("distribution.method.manual")}
											</span>
										</PlatformRow>
									))}
								</ul>
							</TabsContent>
						</Tabs>
					</Section>
				);
			}}
		</Async>
	);
}

const FLAGS = ["images", "video", "links"] as const;

/**
 * One platform: who, what it takes, what the rules say, and where they came
 * from. The three rules are marks rather than a label over a value: most rows
 * answer "check with platform" for all three, and four columns of that made
 * forty-four platforms eight screens of the same word.
 */
function PlatformRow({ platform: p, children }: { platform: PublishingPlatform; children: React.ReactNode }) {
	const t = useT();
	const { locale } = useI18n();
	const unknown = FLAGS.every((flag) => p[flag] === "check");
	return (
		<li className="publishing-platform">
			<div className="publishing-platform-head">
				<a href={p.url} target="_blank" rel="noopener noreferrer">
					<PlatformIdentity platform={p} />
				</a>
				<span className="hint">{p.domain}</span>
				<div className="publishing-platform-action">{children}</div>
			</div>
			<ul className="publishing-platform-facts">
				<li>{t(`distribution.${p.format}`)}</li>
				{unknown ? (
					<li data-state="check">{t("publishing.rulesUnknown")}</li>
				) : (
					FLAGS.map((flag) => (
						<li key={flag} data-state={p[flag]}>
							{t(`distribution.${flag}`)} · {t(`distribution.${p[flag]}`)}
						</li>
					))
				)}
			</ul>
			{p.notes && <p className="publishing-platform-notes">{pick(locale, p.notes)}</p>}
			<div className="publishing-platform-foot">
				{(p.reviewed_at || p.verified_at) && (
					<span>{t("distribution.reviewed", { date: p.reviewed_at ?? p.verified_at ?? "" })}</span>
				)}
				<Popover>
					<PopoverTrigger asChild>
						<Button variant="link" className="h-auto p-0 text-[length:inherit]">
							{t("distribution.sources")}
							<span className="sr-only"> · {p.name}</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent className="platform-sources">
						<strong>
							{p.name} · {t("distribution.sources")}
						</strong>
						{(p.sources?.length ? p.sources : [p.rules_url]).map((url, index) => (
							<a key={url} href={url} target="_blank" rel="noopener noreferrer">
								{index + 1}. {new URL(url).hostname}
							</a>
						))}
						{p.logo_source_url && (
							<a href={p.logo_source_url} target="_blank" rel="noopener noreferrer">
								{t("distribution.logoSource")}
							</a>
						)}
					</PopoverContent>
				</Popover>
			</div>
		</li>
	);
}

function Account({
	entityId,
	platform,
	accounts = [],
}: {
	entityId: string;
	platform: PublishingPlatform;
	accounts?: { id: string; provider_id: string; name: string }[];
}) {
	const t = useT();
	const { editor } = useAccess();
	const contract = platformById(platform.id);
	if (!contract?.credential_provider) return null;
	const mine = accounts.filter((a) => a.provider_id === contract.credential_provider);
	return mine.length > 0 ? (
		<span className="hint">
			{t("publishing.platform.account")} · {mine.map((a) => a.name).join(", ")}
		</span>
	) : (
		<ConnectAccount entityId={entityId} platform={contract} size="sm" variant="outline" disabled={!editor} />
	);
}

function AddPlatform({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const create = useMutation({
		mutationFn: (platform: Parameters<typeof api.addPublishingPlatform>[1]) =>
			api.addPublishingPlatform(entityId, platform),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const [name, setName] = useState("");
	const [url, setUrl] = useState("");
	const [format, setFormat] = useState("article");
	const [flags, setFlags] = useState({ images: "check", video: "check", links: "check" });
	const [limit, setLimit] = useState("");
	const [open, setOpen] = useState(false);
	async function add(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		try {
			await create.mutateAsync({
				id: "",
				name,
				url,
				domain: "",
				region: "global",
				format,
				...flags,
				max_length: limit ? Number(limit) : null,
				rules_url: url,
				verified_at: null,
			});
			setName("");
			setUrl("");
			setOpen(false);
			toast.success(t("distribution.saved"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<Button variant="default" disabled={!editor} onClick={() => setOpen(true)}>
				{t("distribution.add")}
			</Button>
			<ChipModal open={open} onOpenChange={setOpen} dismissDisabled={busy} srTitle={t("distribution.add")}>
				<ChipModalHeader onClose={() => setOpen(false)} closeAriaLabel={t("common.close")}>
					{t("distribution.add")}
				</ChipModalHeader>
				<ChipModalBody className="px-(--s-8)">
					<ModalDescription>{t("distribution.rulesHint")}</ModalDescription>
					<form id="add-publishing-platform" onSubmit={add}>
						<fieldset className="bare-fieldset stack" disabled={!editor || busy}>
							<label className="field" htmlFor="distribution-platform">
								{t("distribution.platform")}
								<Input
									id="distribution-platform"
									required
									maxLength={100}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</label>
							<label className="field" htmlFor="platform-url">
								URL
								<Input id="platform-url" required type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
							</label>
							<div className="field">
								{t("distribution.format")}
								<ChipSelect
									value={format}
									onChange={setFormat}
									disabled={!editor || busy}
									aria-label={t("distribution.format")}
									options={["article", "social", "video"].map((v) => ({ value: v, label: t(`distribution.${v}`) }))}
								/>
							</div>
							{(["images", "video", "links"] as const).map((flag) => (
								<div className="field" key={flag}>
									{t(`distribution.${flag}`)}
									<ChipSelect
										value={flags[flag]}
										onChange={(value) => setFlags({ ...flags, [flag]: value })}
										disabled={!editor || busy}
										aria-label={t(`distribution.${flag}`)}
										options={["yes", "no", "restricted", "check"].map((v) => ({
											value: v,
											label: t(`distribution.${v}`),
										}))}
									/>
								</div>
							))}
							<label className="field" htmlFor="distribution-limit">
								{t("distribution.limit")}
								<Input
									id="distribution-limit"
									type="number"
									min={100}
									max={100000}
									value={limit}
									onChange={(e) => setLimit(e.target.value)}
								/>
							</label>
						</fieldset>
					</form>
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("common.cancel")}
					onCancel={() => setOpen(false)}
					primaryAction={{
						label: t("distribution.add"),
						type: "submit",
						form: "add-publishing-platform",
						disabled: !editor || busy,
					}}
				/>
			</ChipModal>
		</>
	);
}
